<template>
  <div class="BaseTemplateItemTextSetting">
    <h2 class="q-mt-none q-mb-md text-cool-gray-100 fs-17px fw-500">
      {{ t('contents.texts.setting_title') }}
    </h2>
    <k-alert
      class="q-mb-md"
      :title="t('contents.texts.info.title')"
      :description="t('contents.texts.info.description')"
    />

    <template v-if="templateStore.isDigitalMenu">
      <p class="q-mb-sm fs-15px fw-500 text-cool-gray-100">{{ t('item_availability_link') }}</p>
      <k-btn model="secondary" size="sm" :label="t('connect')" @click="hook.handleOpenAvailabilityDialog('item')" />

      <p class="q-mt-md q-mb-sm fs-15px fw-500 text-cool-gray-100">{{ t('item_price_link') }}</p>
      <k-btn model="secondary" size="sm" :label="t('connect')" @click="hook.handleOpenAvailabilityDialog('price')" />

      <BaseTableDialog
        v-model="state.isItemsAvailabilityDialogOpen"
        :title="t('add_item_availability_link')"
        :options="itemsAvailabilityOptions"
        layout-key="addItemAvailabilityDialog"
        :max-column-count="state.itemsAvailabilityOptions.maxColumnCount"
        :default-table-columns="state.itemsAvailabilityOptions.defaultColumns"
        @select="hook.addItemAvailability"
      >
        <template #category="{ col, localProps: { row } }">
          <div class="row items-center gap-12 no-wrap">
            <k-colour-line width="8px" height="24px" :color="row.category_colour ?? 'transparent'" />
            <span class="fs-15px fw-400 text-cool-gray-80 block">{{ col.value }}</span>
          </div>
        </template>
      </BaseTableDialog>
    </template>
    <template v-else>
      <k-radio v-model="templateStore.activeItem.is_field" :label="t('contents.texts.set_custom_text')" :val="false" />

      <div v-if="!templateStore.activeItem.is_field" class="q-mt-12">
        <k-input
          :model-value="templateStore.activeItem.value"
          @update:model-value="templateStore.handleUpdateItemSetting('value', $event)"
        />
      </div>

      <k-radio
        v-model="templateStore.activeItem.is_field"
        class="q-mt-md"
        :label="t('contents.texts.connect_text_to_data_field')"
        :val="true"
        @update:model-value="templateStore.handleUpdateItemSetting('is_field', true)"
      />

      <div v-if="templateStore.activeItem.is_field" class="q-mt-12">
        <k-dropdown
          :model-value="templateStore.activeItem.value"
          :options="
            templateStore.activeLayer.id !== 'receipt___body'
              ? templateStore.templateFields.master
              : templateStore.templateFields.slave
          "
          options-mode="radio"
          placeholder="Select"
          icon="link-2"
          icon-color="info-70"
          :has-clear-icon="false"
          @update:model-value="templateStore.handleUpdateItemSetting('value', $event)"
        >
        </k-dropdown>
      </div>
    </template>

    <k-divider class="q-my-md" size="md" color="cool-gray-40" />

    <h2 class="q-mt-none q-mb-md text-cool-gray-100 fs-17px fw-500">{{ t('contents.styles.setting_title') }}</h2>

    <BaseTemplateItemTransformSetting :has-height="false" />

    <k-divider class="q-my-md" dotted size="sm" color="cool-gray-50" />
    <p class="fs-15px fw-500 text-cool-gray-80 q-mb-sm">{{ t('contents.styles.customise_text') }}</p>
    <div class="flex items-center no-wrap gap-12">
      <k-input
        v-number
        small
        :model-value="templateStore.activeItem.font_size"
        :has-clear-icon="false"
        input-class="text-center"
        @update:model-value="handleFontSizeUpdate"
      />

      <k-icon
        class="cursor-pointer"
        name="bold"
        size="20px"
        :color="templateStore.activeItem.font_weight === 500 ? 'cool-gray-70' : 'cool-gray-100'"
        @click="
          templateStore.handleUpdateItemSetting('font_weight', templateStore.activeItem.font_weight === 700 ? 500 : 700)
        "
      />
      <div class="BaseTemplateItemTextSetting__divider bg-cool-gray-30 rounded-8"></div>

      <k-icon
        class="cursor-pointer"
        name="font"
        size="20px"
        :color="!isFontPickerOpen ? 'cool-gray-70' : 'cool-gray-100'"
        @click="$refs.fontPickerPopup.toggle()"
      />

      <q-popup-proxy ref="fontPickerPopup" v-model="isFontPickerOpen" class="shadow-0" no-parent-event>
        <div class="q-pa-xs BaseTemplateItemTextSetting__font-picker-box">
          <k-dropdown
            :model-value="templateStore.activeItem.font_family"
            :options="hook.fontFamilies"
            :has-clear-icon="false"
            @update:model-value="templateStore.handleUpdateItemSetting('font_family', $event)"
          >
          </k-dropdown>
        </div>
      </q-popup-proxy>

      <div class="BaseTemplateItemTextSetting__divider bg-cool-gray-30 rounded-8"></div>
      <k-icon
        v-for="textAlign in hook.textAligns"
        :key="textAlign"
        class="cursor-pointer"
        size="20px"
        :name="`text-align-${textAlign}`"
        :color="templateStore.activeItem.text_align !== textAlign ? 'cool-gray-70' : 'cool-gray-100'"
        @click="templateStore.handleUpdateItemSetting('text_align', textAlign)"
      />
      <template v-if="!templateStore.isPrintTemplate">
        <div class="BaseTemplateItemTextSetting__divider bg-cool-gray-30 rounded-8"></div>
        <span class="flex">
          <k-icon class="cursor-pointer" name="colours" size="20px" />

          <q-popup-proxy ref="colorPickerPopup" class="hide-scrollbar">
            <k-colour-picker
              style="width: 290px"
              :model-value="templateStore.activeItem.colour"
              :done-label="globalT('save')"
              :cancel-label="globalT('cancel')"
              @cancel="$refs.colorPickerPopup.hide()"
              @update:model-value="templateStore.handleUpdateItemSetting('colour', $event)"
            />
          </q-popup-proxy>
        </span>
      </template>
    </div>
    <k-divider class="q-my-md" dotted size="sm" color="cool-gray-50" />
    <p class="fs-15px fw-500 text-cool-gray-80 q-mb-sm">{{ t('contents.styles.pre_set_text_styles') }}</p>

    <div
      v-for="(font, index) in hook.fonts"
      :key="index"
      class="BaseTemplateItemTextSetting__set-text-box q-pa-md rounded-16 q-mb-sm cursor-pointer"
      :class="{
        'BaseTemplateItemTextSetting__set-text-box--active':
          font.font_size === templateStore.activeItem.font_size &&
          font.font_weight === templateStore.activeItem.font_weight,
      }"
      @click="hook.handlePreSetTextStyle(font)"
    >
      <p
        class="text-cool-gray-100 q-mb-none"
        :style="`font-size: ${font.font_size}px; font-weight: ${font.font_weight}`"
      >
        {{ font.title }}
      </p>
      <p class="fs-12px fw-400 text-cool-gray-80">{{ font.font_size }} px</p>
    </div>
  </div>
</template>

<script setup>
import useBaseTemplateItemTextHook from './BaseTemplateItemTextSettingHook'
import BaseTemplateItemTransformSetting from './BaseTemplateItemTransformSetting'
import translate from 'src/common/helpers/language/translate'
import translationObject from '../../translate/baseTemplate.translate'
import globalTranslateObject from 'src/translate/global.translate'

defineOptions({ name: 'BaseTemplateItemTextSetting' })

const { t } = translate(translationObject)
const { t: globalT } = translate(globalTranslateObject)

const { state, templateStore, itemsAvailabilityOptions, isFontPickerOpen, ...hook } = useBaseTemplateItemTextHook()

function handleFontSizeUpdate(value) {
  const size = +value
  const HEIGHT_PADDING = 8

  templateStore.handleUpdateItemSetting('font_size', size)
  templateStore.handleUpdateItemSetting('height', size + HEIGHT_PADDING)
}
</script>

<style lang="sass" scoped>
@import "src/shared/styles/mixins.sass"

.BaseTemplateItemTextSetting
  @include element('divider')
    height: 24px
    width: 1px
  @include element('set-text-box')
    transition: 0.3s ease
    border: 1px solid var(--off-white-20)
    @include state-modifier('active')
      border-color: var(--space-b-50)
      background-color: var(--teal-white-10)
  @include element('font-picker-box')
    margin-left: -4px
    width: 300px
  @include element('input')
    width: 106px
</style>
